// utils/dataProcessors.js - Extracted data processing utilities

/**
 * Employee data processing utilities
 * Extracted from dashboard components for reusability and performance
 */

export const employeeProcessor = {
  /**
   * Calculate employee summary from time data
   * @param {Array} timeData - Array of time activity entries
   * @returns {Array} Processed employee summary data
   */
  calculateSummary: (timeData) => {
    if (!timeData || !Array.isArray(timeData) || timeData.length === 0) {
      return [];
    }

    const employeeMap = new Map();
    
    timeData.forEach(entry => {
      const employee = entry.employee;
      if (!employee) return;

      if (!employeeMap.has(employee)) {
        employeeMap.set(employee, {
          employee,
          totalHours: 0,
          clientHours: 0,
          nonClientHours: 0,
          billableHours: 0,
          totalRevenue: 0,
          customers: new Set(),
          entries: 0
        });
      }

      const empData = employeeMap.get(employee);
      const hours = parseFloat(entry.total_hours || entry.duration || 0);
      const amount = parseFloat(entry.amount || 0);

      empData.totalHours += hours;
      empData.totalRevenue += amount;
      empData.entries += 1;

      // Handle different data structures for client hours
      if (entry.category === 'Client' || entry.client_hours) {
        empData.clientHours += hours;
      } else {
        empData.nonClientHours += hours;
      }

      if (entry.billable === 'billable') {
        empData.billableHours += hours;
      }

      if (entry.customer) {
        empData.customers.add(entry.customer);
      }
    });

    return Array.from(employeeMap.values()).map(emp => ({
      ...emp,
      customerCount: emp.customers.size,
      utilizationRate: emp.totalHours > 0 ? emp.clientHours / emp.totalHours : 0,
      billingRate: emp.totalHours > 0 ? emp.billableHours / emp.totalHours : 0,
      avgRate: emp.totalHours > 0 ? emp.totalRevenue / emp.totalHours : 0
    }));
  },

  /**
   * Calculate employee-customer matrix from time data
   * @param {Array} timeData - Array of time activity entries
   * @returns {Object} Employee-customer matrix with employees and customers arrays
   */
  calculateMatrix: (timeData) => {
    if (!timeData || !Array.isArray(timeData) || timeData.length === 0) {
      return { matrix: {}, employees: [], customers: [] };
    }

    const matrix = {};
    const employees = new Set();
    const customers = new Set();

    timeData.forEach(entry => {
      const employee = entry.employee;
      const customer = entry.customer || 'Unassigned';
      const hours = parseFloat(entry.total_hours || entry.duration || 0);

      if (!employee) return;

      employees.add(employee);
      customers.add(customer);

      if (!matrix[employee]) {
        matrix[employee] = {};
      }

      if (!matrix[employee][customer]) {
        matrix[employee][customer] = 0;
      }

      matrix[employee][customer] += hours;
    });

    return {
      matrix,
      employees: Array.from(employees).sort(),
      customers: Array.from(customers).sort()
    };
  }
};

/**
 * Customer data processing utilities
 */
export const customerProcessor = {
  /**
   * Calculate customer breakdown from time data
   * @param {Array} timeData - Array of time activity entries
   * @returns {Array} Processed customer breakdown data
   */
  calculateBreakdown: (timeData) => {
    if (!timeData || !Array.isArray(timeData) || timeData.length === 0) {
      return [];
    }

    const customerMap = new Map();

    timeData.forEach(entry => {
      const customer = entry.customer;
      if (!customer) return;

      if (!customerMap.has(customer)) {
        customerMap.set(customer, {
          customer,
          totalHours: 0,
          totalRevenue: 0,
          employees: new Set(),
          entries: 0
        });
      }

      const custData = customerMap.get(customer);
      const hours = parseFloat(entry.total_hours || entry.duration || 0);
      const amount = parseFloat(entry.amount || 0);

      custData.totalHours += hours;
      custData.totalRevenue += amount;
      custData.entries += 1;

      if (entry.employee) {
        custData.employees.add(entry.employee);
      }
    });

    return Array.from(customerMap.values()).map(cust => ({
      ...cust,
      employeeCount: cust.employees.size,
      avgRate: cust.totalHours > 0 ? cust.totalRevenue / cust.totalHours : 0
    }));
  },

  /**
   * Calculate revenue analysis from chart data
   * @param {Array} chartData - Array of revenue chart data
   * @returns {Object} Revenue analysis metrics
   */
  calculateRevenueAnalysis: (chartData) => {
    if (!chartData || !Array.isArray(chartData) || chartData.length === 0) {
      return {
        totalRevenue: 0,
        totalLastYearRevenue: 0,
        averageMonthlyRevenue: 0,
        growthAmount: 0,
        growthPercentage: 0,
        highestMonth: null,
        lowestMonth: null,
        monthCount: 0,
        hasGrowth: false
      };
    }

    const totalRevenue = chartData.reduce((sum, item) => sum + (item.revenue || 0), 0);
    const totalLastYearRevenue = chartData.reduce((sum, item) => sum + (item.last_year_revenue || 0), 0);
    const averageMonthlyRevenue = totalRevenue / chartData.length;
    
    const growthAmount = totalRevenue - totalLastYearRevenue;
    const growthPercentage = totalLastYearRevenue > 0 ? (growthAmount / totalLastYearRevenue) * 100 : 0;

    // Find highest and lowest revenue months
    const highestMonth = chartData.reduce((max, item) => 
      item.revenue > max.revenue ? item : max, chartData[0] || {});
    const lowestMonth = chartData.reduce((min, item) => 
      item.revenue < min.revenue ? item : min, chartData[0] || {});

    return {
      totalRevenue,
      totalLastYearRevenue,
      averageMonthlyRevenue,
      growthAmount,
      growthPercentage,
      highestMonth,
      lowestMonth,
      monthCount: chartData.length,
      hasGrowth: growthAmount > 0
    };
  }
};

/**
 * Date range utilities
 */
export const dateProcessor = {
  /**
   * Create standard date range presets
   * @returns {Object} Date range presets
   */
  getPresets: () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();
    
    return {
      'today': {
        start_date: today.toISOString().split('T')[0],
        end_date: today.toISOString().split('T')[0],
        preset: 'today'
      },
      'this-week': {
        start_date: new Date(today.setDate(today.getDate() - today.getDay())).toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
        preset: 'this-week'
      },
      'this-month': {
        start_date: new Date(currentYear, currentMonth, 1).toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
        preset: 'this-month'
      },
      'this-quarter': {
        start_date: new Date(currentYear, Math.floor(currentMonth / 3) * 3, 1).toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
        preset: 'this-quarter'
      },
      'year-to-date': {
        start_date: new Date(currentYear, 0, 1).toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
        preset: 'year-to-date'
      },
      'last-30-days': {
        start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
        preset: 'last-30-days'
      }
    };
  },

  /**
   * Format date range for display
   * @param {Object} dateRange - Date range object
   * @returns {string} Formatted date range string
   */
  formatDateRange: (dateRange) => {
    if (!dateRange || !dateRange.start_date || !dateRange.end_date) {
      return 'Invalid date range';
    }

    const startDate = new Date(dateRange.start_date);
    const endDate = new Date(dateRange.end_date);
    
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    
    if (dateRange.start_date === dateRange.end_date) {
      return startDate.toLocaleDateString('en-US', options);
    }
    
    return `${startDate.toLocaleDateString('en-US', options)} - ${endDate.toLocaleDateString('en-US', options)}`;
  }
};

/**
 * Performance utilities for memoization and caching
 */
export const performanceUtils = {
  /**
   * Create a memoized version of a function with TTL cache
   * @param {Function} fn - Function to memoize
   * @param {number} ttl - Time to live in milliseconds
   * @returns {Function} Memoized function
   */
  memoizeWithTTL: (fn, ttl = 300000) => { // 5 minutes default
    const cache = new Map();
    
    return (...args) => {
      const key = JSON.stringify(args);
      const cached = cache.get(key);
      
      if (cached && Date.now() - cached.timestamp < ttl) {
        return cached.value;
      }
      
      const result = fn(...args);
      cache.set(key, { value: result, timestamp: Date.now() });
      
      // Clean old entries periodically
      if (cache.size > 100) {
        const now = Date.now();
        for (const [k, v] of cache.entries()) {
          if (now - v.timestamp > ttl) {
            cache.delete(k);
          }
        }
      }
      
      return result;
    };
  },

  /**
   * Debounce function for reducing API calls
   * @param {Function} func - Function to debounce
   * @param {number} wait - Wait time in milliseconds
   * @returns {Function} Debounced function
   */
  debounce: (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  },

  /**
   * Throttle function for limiting execution frequency
   * @param {Function} func - Function to throttle
   * @param {number} limit - Time limit in milliseconds
   * @returns {Function} Throttled function
   */
  throttle: (func, limit) => {
    let inThrottle;
    return function() {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }
};

/**
 * Export utilities for data transformation
 */
export const exportUtils = {
  /**
   * Convert array of objects to CSV string
   * @param {Array} data - Array of objects to convert
   * @param {Array} headers - Optional custom headers
   * @returns {string} CSV string
   */
  arrayToCSV: (data, headers = null) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return '';
    }

    const keys = headers || Object.keys(data[0]);
    const csvHeaders = keys.join(',');
    
    const csvRows = data.map(row => {
      return keys.map(key => {
        const value = row[key];
        // Handle strings with commas or quotes
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value || '';
      }).join(',');
    });

    return [csvHeaders, ...csvRows].join('\n');
  },

  /**
   * Download data as CSV file
   * @param {Array} data - Data to export
   * @param {string} filename - Filename for download
   * @param {Array} headers - Optional custom headers
   */
  downloadCSV: (data, filename, headers = null) => {
    const csv = exportUtils.arrayToCSV(data, headers);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};